import { useMemo } from 'react';
import { useLogoStore } from '@/features/application/store/logo.store';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';
import { useFormBuilderStore } from '@/features/form-builder/store/form-builder.store';
import { useDashboardStore } from '@/features/dashboard/stores/dashboard.store';
import { useDomainsStore } from '@/features/domains/stores/domains.store';
import { logger } from '@/features/help/utils/logger';

/**
 * Custom hook to determine if dashboard prerequisites (logo, field mappings, visited settings) are met.
 * @returns An object containing the status of each prerequisite and an overall status.
 */
export const useDashboardPrerequisites = () => {
  // Select necessary state from stores
  const logoUrl = useLogoStore((state) => state.logoUrl);
  const mappingSummaries = useMappingsStore((state) => state.mappingSummaries);
  const dashboardInitialized = useFormBuilderStore((state) => state.dashboardInitialized);
  const viewed_settings = useDashboardStore((state) => state.viewed_settings);
  const domains = useDomainsStore((state) => state.domains);

  // Derive mapping status from the existence of any saved mappings
  const hasMappings = useMemo(() => {
    const mappingsExist = mappingSummaries.length > 0;

    logger.debug(
      'useDashboardPrerequisites',
      'Calculated mapping status based on available mappings',
      {
        mappingsExist,
        availableMappingCount: mappingSummaries.length,
      }
    );
    return mappingsExist;
  }, [mappingSummaries]);

  // Convert viewed_settings (which can be null initially) to boolean
  const hasViewedSettings = viewed_settings === true;

  // Check if user has added at least one custom domain
  const hasCustomDomain = domains.length > 0;

  // Determine overall prerequisite status
  const prerequisitesMet = dashboardInitialized && !!logoUrl && hasMappings && hasViewedSettings && hasCustomDomain;

  // Log the final status
  logger.debug('useDashboardPrerequisites', 'Prerequisite status', {
    dashboardInitialized,
    hasLogo: !!logoUrl,
    hasMappings,
    hasViewedSettings,
    hasCustomDomain,
    rawViewedSettings: viewed_settings, // Log raw value too
    prerequisitesMet,
  });

  return {
    logoUrl, // Return individual statuses for potential future use
    hasMappings,
    dashboardInitialized,
    hasViewedSettings,
    hasCustomDomain,
    prerequisitesMet, // The combined status
  };
};
